import type { NextPage } from "next";
import Head from "next/head";
import { StartView, FAQ as START_FAQS } from "../views/start";
import {
  SITE_OG_IMAGE,
  SITE_URL,
  breadcrumb,
  graph,
  howTo,
  ld,
  softwareApplication,
  webPage,
} from "../lib/schema";

const siteUrl = `${SITE_URL}/start`;
const siteTitle = "SoulMinter – Create a Solana Token for a Flat 0.05 SOL";
const siteDescription = "Create a Solana token with one flat fee. Custom mint address, on-chain social metadata, and authority revocation are included. No percentage of supply taken. Non-custodial software tool operated by ROTH MATE EV (Hungary, EU).";
const siteImage = SITE_OG_IMAGE;

const trail = [
  { name: "SoulMinter", url: SITE_URL },
  { name: "Start", url: siteUrl },
];

/**
 * Mirrors the three-step <ol> StartView renders under "Three steps, about
 * a minute." Change one and change the other -- a HowTo describing
 * steps a reader cannot see on the page is a guidelines violation, and the
 * value here is entirely in answer engines quoting the real flow.
 */
const createHowTo = howTo({
  id: `${siteUrl}#howto-create`,
  pageUrl: siteUrl,
  name: "How to create a Solana token with SoulMinter",
  description:
    "Create an SPL or Token-2022 mint on Solana mainnet in three steps, without writing code. Flat 0.05 SOL on the Standard tier plus Solana network fees.",
  totalTime: "PT1M",
  steps: [
    {
      name: "Open the Token Studio",
      text: "Fill in the basics — name, ticker, supply, decimals, logo, socials. Pick a vanity prefix or suffix for your mint address if you want one. No account required.",
    },
    {
      name: "Connect a Solana wallet",
      text: "Any wallet that follows the Solana Wallet Standard works. Connection and signing happen in your wallet, locally, in your browser.",
    },
    {
      name: "Sign one transaction",
      text: "Your token lands on Solana mainnet in about a minute — fully on-chain, fully yours. Authorities are revoked in the same transaction if you chose to.",
    },
  ],
});

const startGraph = graph([
  softwareApplication,
  webPage({
    url: siteUrl,
    name: siteTitle,
    description: siteDescription,
    image: siteImage,
    trail,
    faqs: START_FAQS,
  }),
  breadcrumb(siteUrl, trail),
  createHowTo,
]);

const Start: NextPage = () => {
  return (
    <>
      <Head>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="ROTH MATE EV" />
        <meta name="theme-color" content="#0B0F19" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:site_name" content="SoulMinter" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content={siteImage} />

        <link rel="canonical" href={siteUrl} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: ld(startGraph) }}
        />
      </Head>
      <StartView />
    </>
  );
};

export default Start;
